import { useState } from 'react'
import { useLang } from '../i18n'
import type { InstallResult } from '../lib/api'
import { ClaudeUpdateCta } from './ClaudeUpdateCta'

/**
 * A chat turn that failed because the API refused the model: this Claude Code is too old for it
 * (the job carried `errorCode: 'agent_update_required'` + `requiredVersion`, AB#370). Rendered like
 * the sign-in prompt rather than a red error, with the assisted update right here.
 *
 * Blocking, unlike the Settings warning for the 2.1.265 sign-in floor: the chat cannot work at all
 * until Claude Code is updated, so the wording says so — but nothing else is gated.
 *
 * Reuses {@link ClaudeUpdateCta} as-is (confirm → running → done/failed, log tail on failure). Once
 * an update reports a version that meets the floor, Retry resends the same prompt.
 */
export function ChatUpdateRequiredCard({
  requiredVersion,
  currentVersion,
  showCta,
  onUpdated,
  onRetry,
}: {
  requiredVersion: string
  currentVersion: string | undefined
  /** At most one card in the transcript carries the update button (the latest one). */
  showCta: boolean
  /** The update finished — re-read `/agent/status`. */
  onUpdated: () => void
  /** Resend the prompt that failed. */
  onRetry: () => void
}) {
  const { t } = useLang()
  const [updated, setUpdated] = useState(false)
  const title = currentVersion
    ? t('chat_update_required_title').replace('{n}', () => currentVersion).replace('{min}', () => requiredVersion)
    : t('chat_update_required_title_unknown').replace('{min}', () => requiredVersion)
  const [manualBefore, manualRest = ''] = t('chat_update_required_manual').split('{cmd}')
  const [manualMiddle, manualAfter] = manualRest.split('{cmd2}')

  function handleUpdated(result: InstallResult): void {
    // `versionOk === false` is "updated, and still too old": retrying would just fail again, and
    // the CTA itself already says so. Unknown (`undefined`) is not evidence of a problem.
    if (result.versionOk !== false) setUpdated(true)
    onUpdated()
  }

  return (
    <div className="chat-auth-failed" role="status" aria-live="polite">
      <p className="chat-auth-failed-title">{title}</p>
      {!showCta && <p className="chat-auth-failed-reason">{t('chat_update_required_body')}</p>}
      {showCta && (
        <ClaudeUpdateCta
          currentVersion={currentVersion}
          minVersion={requiredVersion}
          note={t('chat_update_required_body')}
          onUpdated={handleUpdated}
        />
      )}
      {updated && (
        <div className="chat-auth-failed-actions">
          <button type="button" className="mini ok" onClick={onRetry}>
            {t('chat_auth_required_retry')}
          </button>
        </div>
      )}
      <p className="chat-auth-failed-reason">
        {manualBefore}
        <code>claude update</code>
        {manualMiddle}
        <code>irm https://claude.ai/install.ps1 | iex</code>
        {manualAfter}
      </p>
    </div>
  )
}
